// ORKSTRA Feature Flags
// Controls safe rollout of new features per user/tenant
//
// Priority order (highest wins):
//   1. User-specific override (USER_FLAGS)
//   2. Tenant-specific override (TENANT_FLAGS)
//   3. Default value (DEFAULT_FLAGS)
//
// Rollout strategy:
//   Day 0: Master only (amr@aletlala.com)
//   Day 1-2: Aletlala tenant (id=1)
//   Day 3+: Selected beta tenants
//   Day 8+: Flip DEFAULT to true

import { useAuthStore } from '@/stores/authStore'

export interface FeatureFlags {
  NEW_NAV_V2: boolean
  COMMAND_PALETTE: boolean
  THEME_SWITCHER: boolean
  AI_SUGGESTIONS: boolean
}

const DEFAULT_FLAGS: FeatureFlags = {
  NEW_NAV_V2: false,
  COMMAND_PALETTE: false,
  THEME_SWITCHER: false,
  AI_SUGGESTIONS: false,
}

// Per-tenant overrides
// Tenant ID 1 = Aletlala (Master's company)
const TENANT_FLAGS: Record<number, Partial<FeatureFlags>> = {
  1: {
    NEW_NAV_V2: true,
    COMMAND_PALETTE: true,
    THEME_SWITCHER: true,
  },
}

// Per-user overrides (highest priority)
// Master gets all features for testing
const USER_FLAGS: Record<string, Partial<FeatureFlags>> = {
  'amr@aletlala.com': {
    NEW_NAV_V2: true,
    COMMAND_PALETTE: true,
    THEME_SWITCHER: true,
    AI_SUGGESTIONS: true,
  },
}

/**
 * Check if a feature flag is enabled for the current user.
 *
 * @param flag - The feature flag name
 * @returns true if enabled, false otherwise
 *
 * @example
 *   const newNavV2 = useFeatureFlag('NEW_NAV_V2')
 *   return newNavV2 ? <NavigationV2 /> : <NavigationV1 />
 */
export function useFeatureFlag(flag: keyof FeatureFlags): boolean {
  const user = useAuthStore((state) => state.user)

  // Priority 1: User-specific override (by email)
  if (user?.email && USER_FLAGS[user.email]?.[flag] !== undefined) {
    return USER_FLAGS[user.email][flag]!
  }

  // Priority 2: Tenant-specific override (by tenant id)
  const tenantId = user?.tenant?.id
  if (tenantId !== undefined && TENANT_FLAGS[tenantId]?.[flag] !== undefined) {
    return TENANT_FLAGS[tenantId][flag]!
  }

  // Priority 3: Default value
  return DEFAULT_FLAGS[flag]
}

/**
 * Get all feature flags (for debugging/admin).
 */
export function getAllFlags(): FeatureFlags {
  return { ...DEFAULT_FLAGS }
}

/**
 * Check feature flag without React hooks (for non-component code).
 */
export function checkFeatureFlag(
  flag: keyof FeatureFlags,
  userEmail?: string | null,
  tenantId?: number | null,
): boolean {
  if (userEmail && USER_FLAGS[userEmail]?.[flag] !== undefined) {
    return USER_FLAGS[userEmail][flag]!
  }
  if (
    tenantId !== undefined &&
    tenantId !== null &&
    TENANT_FLAGS[tenantId]?.[flag] !== undefined
  ) {
    return TENANT_FLAGS[tenantId][flag]!
  }
  return DEFAULT_FLAGS[flag]
}
